import {
  istTeam,
  TASK_STATUS_LABELS,
  ZEITFENSTER_LABELS,
  zeitfensterStand,
  type Zeitfenster,
  MARKIER_FILTER,
  type MarkierFilter,
} from '@netcon-work/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Paperclip } from 'lucide-react'
import { useRef, useState } from 'react'
import { anhangHochladen, anhangVerweis, dateienAus, ziehtDateien } from '../lib/anhang'
import { Markdown } from '../lib/markdown'
import {
  meilensteineQuery,
  sitzungQuery,
  vorhabenAllerWeltenQuery,
  vorhabenKommentareQuery,
  workspacesQuery,
} from '../lib/queries'
import { aktiverWorkspaceIst, trpc } from '../lib/trpc'
import { AufgabeAnlegen } from './AufgabeAnlegen'
import { AufgabenTitel, useEscSchliesst } from './AufgabeKontext'
import { AufgabeZuordnen } from './AufgabeZuordnen'
import { MarkdownFeld } from './MarkdownFeld'
import { MeilensteinFormular, terminText } from './MeilensteinFormular'
import { PinKnopf } from './PinKnopf'
import { Eckdaten, Steuerung, Wer } from './VorhabenFelder'
import { NotizenZumVorhaben } from './NotizenVerknuepft'
import { VorhabenUmgebungen } from './VorhabenUmgebungen'
import { Button } from './ui/primitives'
import { useAufgabenDesVorhabens, useErwaehnbarePersonen } from '../lib/welt'
import { useMerker } from '../lib/merker'
import { MarkierFilterLeiste, SonnenMarke, useMarkierFilter } from './Markierung'
import { hintergrundSchliesst } from './ui/hintergrund'

/**
 * **Das Vorhaben-Detail** (21.08.2026, [E-205](../../../../docs/ENTSCHEIDUNGEN.md)).
 *
 * Nils: *„Vorhaben sollten noch mit Infos, Bildern, Beschreibungen, Kommentaren usw.
 * hinterlegt werden können. Das man ein Vorhaben mit mehreren Personen diskutieren und planen
 * kann, darin dann die Aufgaben anlegen kann."*
 *
 * ## Warum ein eigenes Fenster und nicht mehr Karte
 *
 * Die Übersicht zeigt Karten in einer Liste, und eine Karte ist eine Zusammenfassung: Name,
 * Marken, Fortschritt, Anbindung. Was hier hineingehört – eine Beschreibung mit Bildern, eine
 * Unterhaltung, die Liste der Aufgaben – sprengt sie beim ersten Vorhaben, das ernst gemeint
 * ist. Bei acht Vorhaben untereinander wäre die Übersicht dann keine mehr.
 *
 * Dieselbe Bauform wie das Aufgabendetail, weil es dieselbe Bewegung ist: aus einer Liste in
 * eine Sache hinein und wieder heraus, ohne die Liste zu verlieren.
 *
 * ## Das eine Fenster eines Vorhabens (seit 23.10.2026)
 *
 * Hier stand: *„Pool, Nachfluss, Mit-Owner, Wunsch-Entwickler und Zeitfenster bleiben an der
 * Karte"* – im Einstellungs-Panel neben der Liste. Das war die Doppelung, vor der der Satz warnte:
 * Mit-Owner, Eingeladene und die **Beschreibung** standen am Ende an beiden Stellen. Nils:
 * *„irgendwie gibt es zu viele Fenster … die teilweise alles das selbe machen."* Entschieden:
 * **ein Fenster.** Von oben nach unten: Eckdaten, Beschreibung, Wer, Aufgaben, Meilensteine,
 * Diskussion – und für das Team die Steuerung (Pool, Nachfluss, Löschen). Die Bausteine stehen in
 * `VorhabenFelder.tsx`.
 */
export function VorhabenDetail({
  vorhabenId,
  schliessen,
}: {
  vorhabenId: string
  schliessen: () => void
}) {
  useEscSchliesst(schliessen)

  /**
   * **Das Detail hängt am Vorhaben, nicht am Umschalter** (30.09.2026,
   * [E-372](../../../../docs/ENTSCHEIDUNGEN.md#e-372)).
   *
   * Nils: *„hier gibt es denke ich noch ein problem wenn ich das vorhaben öffnen will"* – und im
   * Bild stand „Dieses Vorhaben gibt es nicht." über einem Vorhaben, an dem gerade eine seiner
   * laufenden Aufgaben hing. Es gab es sehr wohl; es lag nur in einer anderen Arbeitswelt als
   * der, in der der Umschalter stand, und `vorhaben.alle` ist gebunden. Aus „Mein Tag" heraus –
   * der Ansicht, die die Welten bewusst mischt – ist das der Normalfall und nicht die Ausnahme.
   *
   * Wer hier ankommt, hat ein bestimmtes Vorhaben angeklickt; die Grenze, die ihn davon
   * fernhält, ist die Mitgliedschaft (`vorhabenSichtbarkeit` im Router), nicht die Ansicht.
   */
  const { data: vorhaben = [] } = useQuery(vorhabenAllerWeltenQuery)
  const v = vorhaben.find((z) => z.id === vorhabenId)

  const { data: sitzung } = useQuery(sitzungQuery)
  /**
   * **Wer nur zusieht, sieht keine Knöpfe.** Anlegen, Zuordnen und Meilensteine sind
   * `teamProcedure`; ein Gast kommt hier ohnehin nicht her (`vorhabenSichtbarkeit`), ein
   * Mit-Owner schon – und für ihn wären es Knöpfe, die mit einer Fehlermeldung antworten.
   */
  const rolle = sitzung?.role ?? 'entwickler'
  /**
   * **Und wer in einer anderen Arbeitswelt steht, legt hier nichts an** (30.09.2026, E-372).
   *
   * Seit das Detail über die Welten hinweg aufgeht, kann es ein Vorhaben zeigen, das nicht in
   * der Welt des Umschalters liegt. Lesen, kommentieren und Meilensteine setzen ist daran
   * gebunden, wem das Vorhaben gehört – das prüft der Router. **Anlegen und Zuordnen** hängen
   * dagegen am Umschalter: `AufgabeAnlegen` legt in der aktiven Welt an und `AufgabeZuordnen`
   * bietet deren Aufgaben an. Beides führte hier zu einer Aufgabe in der einen Welt an einem
   * Vorhaben der anderen. Also sagt die Zeile, was zu tun ist, statt einen Knopf anzubieten,
   * der es falsch macht.
   */
  const { data: welten = [] } = useQuery(workspacesQuery)
  const aktiveWelt = aktiverWorkspaceIst()
  const andereWelt =
    v && aktiveWelt && v.workspaceId !== aktiveWelt
      ? (welten.find((w) => w.id === v.workspaceId)?.label ?? 'einer anderen Arbeitswelt')
      : null
  /**
   * **Team oder eingeladen** ([E-440](../../../docs/ENTSCHEIDUNGEN.md#e-440)). Eine Eingeladene
   * darf am Vorhaben alles, was Inhalt ist; Aufgaben anlegen und zuordnen bleibt beim Team,
   * weil es Pools, Welten und Bearbeiter berührt.
   */
  const team = istTeam(rolle)
  const darfAendern = team || Boolean(v?.eingeladen)

  const [anlegenOffen, setAnlegenOffen] = useState(false)
  const [zuordnenOffen, setZuordnenOffen] = useState(false)

  const anteil = v && v.stand.gesamt > 0 ? v.stand.erledigt / v.stand.gesamt : 0

  return (
    <>
      <div
        className="fixed inset-x-0 bottom-0 unter-leiste z-50 flex items-start justify-center bg-[rgb(23_22_20/0.38)] p-3 pt-[4vh] sm:p-6 sm:pt-[6vh]"
        {...hintergrundSchliesst(schliessen)}
        role="dialog"
        aria-modal="true"
        aria-label="Vorhaben"
      >
        <div className="flex max-h-[min(90vh,100%)] sm:max-h-[min(88vh,100%)] w-full min-w-0 max-w-[820px] flex-col rounded-card border border-line bg-surface shadow-card">
          <div className="flex items-start gap-2 border-b border-line px-[18px] py-3.5">
            <div className="min-w-0">
              <h3 className="text-sm font-bold break-words">{v?.label ?? 'Vorhaben'}</h3>
              {v && (
                <>
                  {/**
                   * **Im Kopf steht nur noch der Stand** (23.10.2026). Typ, Entwicklungsart,
                   * Mit-Owner und Eingeladene sind Felder im Fenster darunter – vorher standen sie
                   * hier als Text *und* im Einstellungs-Panel als Feld.
                   */}
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11.5px] text-ink2">
                    <span className="tabular">
                      {v.stand.erledigt} / {v.stand.gesamt} erledigt
                      {v.zieltermin
                        ? ` · Ziel ${new Date(v.zieltermin).toLocaleDateString('de-DE')}`
                        : ''}
                    </span>
                    {v.zeitfenster &&
                      (() => {
                        const stand = zeitfensterStand(v.zeitfenster, v.zeitfensterAb ?? null)
                        return stand.datum === null ? null : (
                          <span className={stand.abgelaufen ? 'text-pool-sofort' : 'text-muted'}>
                            {ZEITFENSTER_LABELS[v.zeitfenster as Zeitfenster] ?? v.zeitfenster}
                            {stand.abgelaufen
                              ? ` · überfällig seit ${stand.datum}`
                              : ` · bis ${stand.datum}`}
                          </span>
                        )
                      })()}
                    {team && (
                      <span className="text-muted">
                        · {v.poolLabel ? `${v.poolIcon ?? ''} ${v.poolLabel}` : 'kein Pool'}
                      </span>
                    )}
                  </div>

                  {v.stand.gesamt > 0 && (
                    <div className="mt-2 h-[5px] w-[240px] overflow-hidden rounded-full bg-accent-soft">
                      <i
                        className="block h-full rounded-full bg-accent"
                        style={{ width: `${anteil * 100}%` }}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
            {/* Ein Vorhaben lässt sich genauso anpinnen wie eine Aufgabe (Z-03) – Pins sind Teamsache. */}
            <div className="ml-auto shrink-0">{team && <PinKnopf vorhabenId={vorhabenId} />}</div>
            <button
              type="button"
              onClick={schliessen}
              className="shrink-0 cursor-pointer rounded-lg px-2 py-1 text-ink2 hover:bg-[#f3f2ef]"
              aria-label="Schließen"
            >
              ✕
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-[18px] pt-1 pb-4">
            {!v && <p className="mt-3 text-[13px] text-muted">Dieses Vorhaben gibt es nicht.</p>}

            {v && (
              <>
                {team && <VonClaudeGeplant vorhabenId={v.id} />}

                <Eckdaten v={v} darfAendern={darfAendern} />

                <Beschreibung vorhabenId={v.id} text={v.beschreibung} darfAendern={darfAendern} />

                <Wer v={v} team={team} />

                <Aufgaben
                  vorhabenId={v.id}
                  darfAendern={team && !andereWelt}
                  {...(andereWelt ? { andereWelt } : {})}
                  neueAufgabe={() => setAnlegenOffen(true)}
                  zuordnen={() => setZuordnenOffen(true)}
                />

                {/* Repo, Coolify und Domain als bewusst gesetzte Verbindung (E-443) – anlegen nur das Team. */}
                <VorhabenUmgebungen
                  vorhabenId={v.id}
                  vorhabenLabel={v.label}
                  darfAendern={team && !andereWelt}
                />

                {/* Was im Notizbuch dazu steht – nur, was man selbst lesen darf (25.10.2026) */}
                <NotizenZumVorhaben vorhabenId={v.id} workspaceId={v.workspaceId ?? null} />

                <Meilensteine vorhabenId={v.id} darfAendern={darfAendern} />

                <Diskussion vorhabenId={v.id} />

                {team && <Steuerung v={v} geloescht={schliessen} />}
              </>
            )}
          </div>
        </div>
      </div>

      {anlegenOffen && (
        <AufgabeAnlegen schliessen={() => setAnlegenOffen(false)} vorbelegt={{ vorhabenId }} />
      )}
      {zuordnenOffen && v && (
        <AufgabeZuordnen
          vorhabenId={v.id}
          vorhabenLabel={v.label}
          schliessen={() => setZuordnenOffen(false)}
        />
      )}
    </>
  )
}

/**
 * **Dateien in ein Textfeld** – ziehen, einfügen oder aussuchen (21.08.2026; seit dem
 * 24.08.2026 nicht mehr nur Bilder, [E-217](../../../../docs/ENTSCHEIDUNGEN.md#e-217)).
 *
 * Wortgleich zum Weg am Aufgabendetail, nur an ein Vorhaben gehängt: die Datei wird
 * hochgeladen und ihr Verweis in den **Entwurf** geschrieben, nicht sofort abgeschickt. Wer
 * ein Bild einfügt, will meistens noch etwas dazuschreiben.
 *
 * Zurück kommen die Ereignis-Hänger als Bündel, damit die Aufrufstelle sie nur noch
 * ausbreiten muss – ohne sie an einer der beiden Stellen zu vergessen. **Der Knopf kommt aus
 * demselben Grund fertig zurück**: er braucht ein verstecktes `<input>` neben sich, und zwei
 * Aufrufstellen, die beides von Hand zusammensetzen, sind zwei Gelegenheiten für einen
 * Unterschied.
 */
function useAnhangAblage(vorhabenId: string, einfuegen: (verweis: string) => void) {
  const [ueberZone, setUeberZone] = useState(false)
  const [laedtHoch, setLaedtHoch] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const dateiFeld = useRef<HTMLInputElement>(null)

  async function hochladen(dateien: File[]): Promise<void> {
    setLaedtHoch(true)
    try {
      for (const datei of dateien) {
        einfuegen(anhangVerweis(await anhangHochladen({ vorhabenId }, datei)))
      }
      setFehler(null)
    } catch (problem) {
      setFehler(problem instanceof Error ? problem.message : 'Hochladen fehlgeschlagen')
    } finally {
      setLaedtHoch(false)
    }
  }

  return {
    ueberZone,
    laedtHoch,
    fehler,
    /**
     * **Abgefangen wird am Umschlag, nicht am Textfeld.** Wer eine Datei auf einen
     * Kommentarbereich zieht, zielt auf „hier" und nicht auf ein 300 Pixel breites Feld.
     */
    haenger: {
      onDragOver: (e: React.DragEvent) => {
        /** `types` statt Inhalt – der ist während `dragover` gesperrt, siehe `ziehtDateien`. */
        if (ziehtDateien(e.dataTransfer)) {
          e.preventDefault()
          setUeberZone(true)
        }
      },
      onDragLeave: () => setUeberZone(false),
      onDrop: (e: React.DragEvent) => {
        const dateien = dateienAus(e.dataTransfer)
        if (dateien.length === 0) return
        e.preventDefault()
        setUeberZone(false)
        void hochladen(dateien)
      },
      onPaste: (e: React.ClipboardEvent) => {
        /** Der wichtigere Weg: ein Screenshot liegt in der Zwischenablage, nicht als Datei. */
        const dateien = dateienAus(e.clipboardData)
        if (dateien.length === 0) return
        e.preventDefault()
        void hochladen(dateien)
      },
    },
    /** Der sichtbare Weg – Ziehen und Einfügen muss man kennen, einen Knopf sieht man. */
    knopf: (
      <>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="inline-flex items-center gap-1"
          disabled={laedtHoch}
          onClick={() => dateiFeld.current?.click()}
        >
          <Paperclip className="size-3.5" aria-hidden /> Datei anhängen
        </Button>
        <input
          ref={dateiFeld}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            const dateien = [...(e.target.files ?? [])]
            /** Zurücksetzen, sonst meldet dieselbe Datei beim zweiten Mal kein `change`. */
            e.target.value = ''
            if (dateien.length > 0) void hochladen(dateien)
          }}
        />
      </>
    ),
  }
}

/**
 * **Worum es geht** – Markdown mit Bildern.
 *
 * Bis zum 21.08.2026 war das ein `<textarea rows={2}>` an der Karte, gespeichert beim
 * Verlassen des Feldes, und angezeigt als Fließtext ohne Auszeichnung. Für den einen Satz aus
 * dem Mockup reichte das; für *„Infos, Bildern, Beschreibungen"* nicht.
 *
 * Der Knopf steht **neben** der Überschrift und nicht in ihr – sonst hieße die Überschrift
 * „Worum geht es? bearbeiten", und wer nach der Überschrift sucht, fände sie nicht.
 */
function Beschreibung({
  vorhabenId,
  text,
  darfAendern,
}: {
  vorhabenId: string
  text: string | null
  darfAendern: boolean
}) {
  const queryClient = useQueryClient()
  const [bearbeiten, setBearbeiten] = useState(false)
  const [entwurf, setEntwurf] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)

  const anhang = useAnhangAblage(vorhabenId, (verweis) =>
    setEntwurf((bisher) => `${bisher}${bisher ? '\n\n' : ''}${verweis}`),
  )

  const speichern = useMutation({
    mutationFn: () =>
      trpc.vorhaben.aendern.mutate({ id: vorhabenId, beschreibung: entwurf.trim() || null }),
    onSuccess: async () => {
      setBearbeiten(false)
      setFehler(null)
      await queryClient.invalidateQueries({ queryKey: ['vorhaben'] })
    },
    onError: (e: Error) => setFehler(e.message),
  })

  if (bearbeiten) {
    return (
      <div className="mt-3" {...anhang.haenger}>
        <h4 className="mb-1.5 text-xs font-bold">Worum geht es?</h4>
        <div
          className={
            anhang.ueberZone ? 'rounded-lg outline-2 outline-offset-2 outline-accent' : undefined
          }
        >
          <MarkdownFeld
            wert={entwurf}
            setzen={setEntwurf}
            zeilen={10}
            platzhalter="Worum geht es – und was ist es nicht? Markdown wird dargestellt, Dateien hierher ziehen oder einfügen."
            beiAbsenden={() => speichern.mutate()}
          />
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <Button
            variant="primary"
            disabled={speichern.isPending}
            onClick={() => speichern.mutate()}
          >
            {speichern.isPending ? 'Speichert …' : 'Speichern'}
          </Button>
          <Button onClick={() => setBearbeiten(false)}>Abbrechen</Button>
          {anhang.knopf}
          <span className="text-[11px] text-muted">
            {anhang.laedtHoch
              ? 'Wird hochgeladen …'
              : 'Markdown · ⌘↵ speichert · Datei ziehen oder einfügen'}
          </span>
        </div>
        {(fehler ?? anhang.fehler) && (
          <p className="mt-1.5 text-[12px] text-pool-sofort">{fehler ?? anhang.fehler}</p>
        )}
      </div>
    )
  }

  return (
    <div className="mt-3">
      <div className="mb-1.5 flex items-baseline gap-2">
        <h4 className="text-xs font-bold">Worum geht es?</h4>
        {darfAendern && (
          <button
            type="button"
            onClick={() => {
              setEntwurf(text ?? '')
              setBearbeiten(true)
            }}
            className="cursor-pointer text-[11px] font-normal text-accent-deep hover:underline"
          >
            bearbeiten
          </button>
        )}
      </div>
      {text ? (
        <div className="text-[13px] text-ink2">
          <Markdown text={text} />
        </div>
      ) : (
        <p className="text-[13px] text-muted">
          Noch nichts beschrieben. Ein Vorhaben ohne Beschreibung ist ein Name – und Namen streiten
          sich.
        </p>
      )}
    </div>
  )
}

/**
 * **Was im Vorhaben steckt** – und die zwei Wege, etwas hineinzubekommen.
 *
 * Nils, 21.08.2026: *„über Vorhaben auch neben + Meilenstein → + Aufgabe und + Aufgabe
 * zuweisen (aus bestehenden)"*. Die Unterscheidung ist der Punkt: **anlegen** heißt, es gibt
 * die Arbeit noch nicht; **zuordnen** heißt, sie liegt schon im Bestand und gehört hierher.
 * Vorher gab es nur den zweiten Weg, und der führte über „Alle Aufgaben" und einen Filter.
 *
 * Offene zuerst – das Erledigte ist beim Nachschlagen selten die Frage.
 */
function Aufgaben({
  vorhabenId,
  darfAendern,
  andereWelt,
  neueAufgabe,
  zuordnen,
}: {
  vorhabenId: string
  darfAendern: boolean
  /** Der Name der Arbeitswelt, in der dieses Vorhaben liegt – nur gesetzt, wenn es eine andere ist. */
  andereWelt?: string
  neueAufgabe: () => void
  zuordnen: () => void
}) {
  /**
   * **Ohne Weltfilter, und der Hook sagt warum** (30.09.2026, E-372): die Zugehörigkeit zum
   * Vorhaben ist die schärfere Bedingung. Der Umschalter hätte im Detail eines Vorhabens aus
   * einer anderen Arbeitswelt schlicht die ganze Liste geleert.
   */
  const meine = useAufgabenDesVorhabens(vorhabenId)
  const offene = meine.filter((a) => a.status !== 'erledigt' && a.status !== 'zurueckgestellt')
  const rest = meine.filter((a) => a.status === 'erledigt' || a.status === 'zurueckgestellt')
  /**
   * **„Mein Tag"-Filter** (Z-20) – dieselbe Reihe wie in „Alle Aufgaben" und den Pools. Mit
   * gesetztem Filter fällt das Erledigte weg: es hat keine Markierung mehr, die zählt.
   */
  const [meinTag, setMeinTag] = useMerker<MarkierFilter | 'alle'>('vorhaben-meintag', 'alle', [
    'alle',
    ...MARKIER_FILTER,
  ])
  const passt = useMarkierFilter()
  const angezeigt =
    meinTag === 'alle' ? [...offene, ...rest] : offene.filter((a) => passt(a.id, meinTag))

  return (
    <div className="mt-4">
      <div className="mb-1.5 flex flex-wrap items-baseline gap-2">
        <h4 className="text-xs font-bold">Aufgaben</h4>
        <span className="text-[11px] text-muted">
          {meine.length === 0 ? 'noch keine' : `${offene.length} offen von ${meine.length}`}
        </span>
        <MarkierFilterLeiste taskIds={offene.map((a) => a.id)} wert={meinTag} setzen={setMeinTag} />
        {andereWelt && (
          <span className="ml-auto text-[11px] text-muted">
            liegt in „{andereWelt}" – zum Anlegen dorthin wechseln
          </span>
        )}
        {darfAendern && (
          <>
            <button
              type="button"
              onClick={neueAufgabe}
              className="ml-auto cursor-pointer text-[11.5px] text-accent-deep hover:underline"
            >
              + Aufgabe
            </button>
            <button
              type="button"
              onClick={zuordnen}
              className="cursor-pointer text-[11.5px] text-accent-deep hover:underline"
            >
              + Aufgabe zuweisen
            </button>
          </>
        )}
      </div>

      {meine.length === 0 ? (
        <p className="text-[12.5px] text-muted">
          Noch nichts drin. „+ Aufgabe" legt eine neue an, „+ Aufgabe zuweisen" holt eine aus dem
          Bestand herüber.
        </p>
      ) : (
        angezeigt.map((a) => (
          <div
            key={a.id}
            className={`flex flex-wrap items-center gap-2 border-b border-line py-1 text-[12.5px] last:border-b-0 ${
              a.status === 'erledigt' ? 'opacity-55' : ''
            }`}
          >
            <span className="w-[86px] shrink-0 text-[11px] text-muted">
              {TASK_STATUS_LABELS[a.status] ?? a.status}
            </span>
            <AufgabenTitel aufgabeId={a.id} titel={a.title} className="min-w-0 flex-1" kuerzen />
            {a.status !== 'erledigt' && <SonnenMarke taskId={a.id} klein />}
            <span className="shrink-0 text-[11px] text-muted">{a.assigneeName ?? 'niemand'}</span>
          </div>
        ))
      )}
    </div>
  )
}

/**
 * **Meilensteine** – dieselbe Liste wie an der Karte, nur mit Platz.
 *
 * Ein Meilenstein ist kein Container, sondern ein Datum mit einer Bedeutung: Aufgaben zeigen
 * darauf, keine, eine oder viele. Deshalb steht an jedem, wie viele es sind – und null ist ein
 * gültiger Wert.
 *
 * **Änderbar und ohne Zwang zum Datum** (28.08.2026, [E-273](../../../../docs/ENTSCHEIDUNGEN.md#e-273)):
 * ein Termin verschiebt sich häufiger, als ein Meilenstein wegfällt, und er steht oft später
 * fest als der Meilenstein selbst. Beides erledigt derselbe Zeileneditor – ein Klick auf den
 * Namen macht aus der Zeile das Formular, das sie anlegen würde.
 */
function Meilensteine({ vorhabenId, darfAendern }: { vorhabenId: string; darfAendern: boolean }) {
  const queryClient = useQueryClient()
  const { data: alle = [] } = useQuery(meilensteineQuery)
  const meine = alle.filter((m) => m.vorhabenId === vorhabenId)

  const [offen, setOffen] = useState(false)
  /** Welche Zeile gerade bearbeitet wird – höchstens eine, sonst wird die Liste zum Formular. */
  const [bearbeitet, setBearbeitet] = useState<string | null>(null)

  const neuLaden = () => queryClient.invalidateQueries({ queryKey: ['vorhaben'] })
  const anlegen = useMutation({
    mutationFn: (m: { label: string; termin: string | null }) =>
      trpc.vorhaben.meilensteinAnlegen.mutate({ vorhabenId, ...m }),
    onSuccess: async () => {
      setOffen(false)
      await neuLaden()
    },
  })
  const aendern = useMutation({
    mutationFn: (m: { id: string; label: string; termin: string | null }) =>
      trpc.vorhaben.meilensteinAendern.mutate(m),
    onSuccess: async () => {
      setBearbeitet(null)
      await neuLaden()
    },
  })
  const erreicht = useMutation({
    mutationFn: (m: { id: string; erreicht: boolean }) =>
      trpc.vorhaben.meilensteinErreicht.mutate(m),
    onSuccess: neuLaden,
  })
  const entfernen = useMutation({
    mutationFn: (id: string) => trpc.vorhaben.meilensteinEntfernen.mutate({ id }),
    onSuccess: async () => {
      setBearbeitet(null)
      await neuLaden()
    },
  })

  return (
    <div className="mt-4">
      <div className="mb-1.5 flex items-baseline gap-2">
        <h4 className="text-xs font-bold">Meilensteine</h4>
        {darfAendern && !offen && (
          <button
            type="button"
            onClick={() => setOffen(true)}
            className="ml-auto cursor-pointer text-[11.5px] text-accent-deep hover:underline"
          >
            + Meilenstein
          </button>
        )}
      </div>

      {meine.length === 0 && !offen && (
        <p className="text-[12.5px] text-muted">Keine – das ist ein gültiger Zustand.</p>
      )}

      {meine.map((m) =>
        bearbeitet === m.id ? (
          <MeilensteinFormular
            key={m.id}
            label={m.label}
            termin={m.termin}
            laeuft={aendern.isPending || entfernen.isPending}
            aktion="Speichern"
            onSpeichern={(werte) => aendern.mutate({ id: m.id, ...werte })}
            onAbbrechen={() => setBearbeitet(null)}
            onEntfernen={() => entfernen.mutate(m.id)}
          />
        ) : (
          <div key={m.id} className="flex flex-wrap items-baseline gap-2 py-0.5 text-[12px]">
            <input
              type="checkbox"
              checked={m.erreichtAm !== null}
              disabled={!darfAendern}
              onChange={(e) => erreicht.mutate({ id: m.id, erreicht: e.target.checked })}
              aria-label={`${m.label} erreicht`}
              className="cursor-pointer"
            />
            {darfAendern ? (
              <button
                type="button"
                onClick={() => setBearbeitet(m.id)}
                title="Meilenstein ändern"
                className={`cursor-pointer text-left hover:underline ${
                  m.erreichtAm ? 'text-muted line-through' : 'font-semibold'
                }`}
              >
                ◆ {m.label}
              </button>
            ) : (
              <span className={m.erreichtAm ? 'text-muted line-through' : 'font-semibold'}>
                ◆ {m.label}
              </span>
            )}
            <span className="text-muted">
              {terminText(m.termin)}
              {m.aufgaben === 0 ? ' · keine Aufgaben' : ` · ${m.offen} von ${m.aufgaben} offen`}
            </span>
          </div>
        ),
      )}

      {offen && (
        <MeilensteinFormular
          label=""
          termin={null}
          laeuft={anlegen.isPending}
          aktion="Hinzufügen"
          onSpeichern={(werte) => anlegen.mutate(werte)}
          onAbbrechen={() => setOffen(false)}
        />
      )}
    </div>
  )
}

/**
 * **Die Unterhaltung** (Nils: *„mit mehreren Personen diskutieren und planen"*).
 *
 * Dieselbe Bedienung wie an der Aufgabe – `@` erwähnt, ⌘↵ sendet, ein Bild wird gezogen oder
 * eingefügt –, damit niemand zwei Kommentarfelder lernen muss.
 *
 * **Ein Unterschied, und der steht auch da:** was hier geschrieben wird, geht nicht nach GitLab
 * oder NetCon. Ein Vorhaben gehört Work und hat dort kein Gegenstück
 * ([E-34](../../../../docs/ENTSCHEIDUNGEN.md)) – eine Unterhaltung, von der man annimmt, der
 * Kunde läse mit, wird anders geführt als eine, von der man es weiß.
 */
function Diskussion({ vorhabenId }: { vorhabenId: string }) {
  const queryClient = useQueryClient()
  const { data: kommentare = [] } = useQuery(vorhabenKommentareQuery(vorhabenId))
  const [entwurf, setEntwurf] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)

  /**
   * **Erwähnbar ist, wer erreichbar ist** – Konto oder Adresse, wie am Aufgabendetail. Und
   * seit dem 16.09.2026 (E-355) **in jeder Welt**: die Verengung auf Mitglieder stand hier seit
   * Z-07 und schloss genau die Gäste aus, die man in einer Diskussion dazuholen will.
   */
  const erwaehnbar = useErwaehnbarePersonen()

  const anhang = useAnhangAblage(vorhabenId, (verweis) =>
    setEntwurf((bisher) => `${bisher}${bisher ? '\n\n' : ''}${verweis}`),
  )

  const schreiben = useMutation({
    mutationFn: (body: string) => trpc.vorhaben.kommentarSchreiben.mutate({ vorhabenId, body }),
    onSuccess: async () => {
      setEntwurf('')
      setFehler(null)
      await queryClient.invalidateQueries({ queryKey: ['vorhaben', 'kommentare', vorhabenId] })
    },
    onError: (e: Error) => setFehler(e.message),
  })

  function absenden() {
    if (entwurf.trim()) schreiben.mutate(entwurf.trim())
  }

  return (
    <div className="mt-4">
      <h4 className="mb-1.5 text-xs font-bold">Diskussion</h4>

      {kommentare.length === 0 && (
        <p className="text-[12.5px] text-muted">
          Noch nichts besprochen. Hier gehört hin, was das ganze Vorhaben angeht – Fragen zu einer
          einzelnen Aufgabe stehen besser an ihr.
        </p>
      )}

      {kommentare.map((k) => (
        <div key={k.id} className="border-b border-line py-2 last:border-b-0">
          <div className="flex items-baseline gap-2 text-[11px] text-muted">
            <strong className="text-ink2">{k.authorName ?? 'unbekannt'}</strong>
            {new Date(k.createdAt).toLocaleString('de-DE', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </div>
          <div className="mt-1 text-[12.5px]">
            <Markdown text={k.body} kompakt />
          </div>
        </div>
      ))}

      <form
        className="mt-2"
        onSubmit={(e) => {
          e.preventDefault()
          absenden()
        }}
        {...anhang.haenger}
      >
        <div
          className={
            anhang.ueberZone ? 'rounded-lg outline-2 outline-offset-2 outline-accent' : undefined
          }
        >
          <MarkdownFeld
            wert={entwurf}
            setzen={setEntwurf}
            zeilen={2}
            erwaehnbar={erwaehnbar}
            platzhalter="Zum Vorhaben schreiben – @ erwähnt jemanden, Datei hierher ziehen oder einfügen …"
            beiAbsenden={absenden}
          />
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <Button type="submit" variant="primary" size="sm" disabled={schreiben.isPending}>
            Kommentieren
          </Button>
          {anhang.knopf}
          <span className="text-[11px] text-muted">
            {anhang.laedtHoch
              ? 'Wird hochgeladen …'
              : 'Bleibt in Work – ein Vorhaben hat in GitLab und NetCon kein Gegenstück.'}
          </span>
        </div>
        {(fehler ?? anhang.fehler) && (
          <p className="mt-1.5 text-[12px] text-pool-sofort">{fehler ?? anhang.fehler}</p>
        )}
      </form>
    </div>
  )
}

/**
 * **„✦ von Claude geplant"** ([Z-25](../../../../docs/phasen/zusatz-25-claude-planung.md) AP 25.10) –
 * eine kleine Marke, wenn Vorhaben oder Teile davon aus einem übernommenen Entwurf stammen. Den
 * Link auf den Entwurf bekommt nur, wem er gehört.
 */
function VonClaudeGeplant({ vorhabenId }: { vorhabenId: string }) {
  const { data = [] } = useQuery({
    queryKey: ['planEntwurf', 'zuVorhaben', vorhabenId],
    queryFn: () => trpc.planEntwurf.zuVorhaben.query({ vorhabenId }),
    staleTime: 60_000,
  })
  if (data.length === 0) return null
  return (
    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-[#5b3fa8]">
      {data.map((e, i) => (
        <span key={i}>
          ✦ von Claude geplant, übernommen von {e.von}
          {e.am ? ` am ${new Date(e.am).toLocaleDateString('de-DE')}` : ''}
          {e.entwurfId && (
            <>
              {' · '}
              <a href={`/entwurf/${e.entwurfId}`} className="font-semibold underline">
                Entwurf
              </a>
            </>
          )}
        </span>
      ))}
    </div>
  )
}
